import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { client, PermissionsDto, UserDetailsDto } from "@/client";
import { Loader } from "@/components/custom/loader";
import { asPositiveInt } from "@/lib/utils";
import { setErrorToastSuperAdmin } from "@/state/error-toast";

const SuperAdminGuard = (
  permissions: PermissionsDto,
  isSuperAdmin: boolean,
): PermissionsDto => {
  const handler = {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (isSuperAdmin) {
        if (value !== null && typeof value === "object") {
          return new Proxy(value, handler);
        }
        return true;
      }

      if (value !== null && typeof value === "object") {
        return new Proxy(value, handler);
      }
      return value;
    },
  };

  return new Proxy(permissions, handler);
};
interface PermissionsContextValue {
  permissions: PermissionsDto;
  override: PermissionsDto | null;
  setOverride: React.Dispatch<React.SetStateAction<PermissionsDto | null>>;
  selectedRole: UserDetailsDto["role"] | null;
  setSelectedRole: React.Dispatch<
    React.SetStateAction<UserDetailsDto["role"] | null>
  >;
}

export const PermissionsContext =
  React.createContext<PermissionsContextValue | null>(null);

export const PermissionsProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { data: user } = useQuery(client.users.me.asQueryOptions());
  const { data: rawPermissions } = useQuery(
    client.permissions.getUser.asQueryOptions(),
  );

  // Fetch default permissions template from server
  const {
    data: defaultPermissions,
    error: templateError,
    isLoading: templateLoading,
  } = useQuery({
    ...client.permissions.getTemplate.asQueryOptions(),
    retry: false,
    throwOnError: false,
  });

  const [selectedRole, setSelectedRole] = useState<
    UserDetailsDto["role"] | null
  >(user?.role ?? null);

  const [override, setOverride] = useState<PermissionsDto | null>(null);

  useEffect(() => {
    if (user?.role) setSelectedRole(user.role);
  }, [user?.role]);

  // The failed-request toast handler sits on the query cache, outside the React tree, so it can't
  // read this context -- push the answer to it instead. Keyed off selectedRole, not user.role, so
  // a SuperAdmin previewing another role sees exactly what that role would see, same as the
  // permissions proxy above.
  useEffect(() => {
    setErrorToastSuperAdmin(selectedRole === "SuperAdmin");
    return () => setErrorToastSuperAdmin(false);
  }, [selectedRole]);

  const base = rawPermissions ?? defaultPermissions;

  // wrapper - if user is SuperAdmin, return true regardless of permissions object
  const permissions = React.useMemo(() => {
    if (!base) return {} as PermissionsDto;
    return SuperAdminGuard(override ?? base, selectedRole === "SuperAdmin");
  }, [override, base, selectedRole]);

  // We can't get the default permissions structure and have nothing cached to
  // fall back on -- the app can't function without it, so hand over to the
  // reconnect page with the failure reason. A failed background refetch that
  // still has usable data behind it must NOT throw the user out mid-session,
  // hence the !defaultPermissions guard.
  if (templateError && !templateLoading && !defaultPermissions) {
    // The reconnect page retries this same request, so it needs the account
    // context the interceptor reads off the URL (forwarded as viewAccountId) --
    // otherwise its retry isn't the request that just failed.
    const accountId = asPositiveInt(
      new URLSearchParams(window.location.search).get("accountId"),
    );
    return (
      <Navigate
        to="/unresponsive"
        replace
        search={{
          context: `${window.location.pathname}${window.location.search ?? ""}`,
          reason: "permissions",
          accountId,
        }}
      />
    );
  }

  // Show loading state while fetching template
  if (templateLoading || !defaultPermissions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="md" />
      </div>
    );
  }

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        override,
        setOverride,
        selectedRole,
        setSelectedRole,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
